import { apiBaseUrl, subdomain } from "@/config/api";
import { KbIssue } from "@/sharedTypes";
import { baseApi } from "../baseApi";
import { flattenQueryParams } from "@/utils/requestUtils";

const kbIssueTag = "KbIssue";

// Standalone PDF download (RTK Query doesn't handle blob responses well).
// Optionally scoped to a createdAt date range (YYYY-MM-DD).
export const downloadKbIssuesReportPdf = async (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<Blob> => {
  // Build local timestamp (DD/MM/YYYY HH:mm) so the PDF reflects the user's
  // local time, not the server's timezone.
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  const generatedAt = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

  const search = new URLSearchParams({ generated_at: generatedAt });
  if (params?.startDate) search.set("startDate", params.startDate);
  if (params?.endDate) search.set("endDate", params.endDate);

  const response = await fetch(
    `${apiBaseUrl}/gw/kb-issues/report/pdf?${search.toString()}`,
    {
      method: "GET",
      credentials: "include",
      headers: subdomain ? { "X-Subdomain": subdomain } : {},
    }
  );

  if (!response.ok) {
    // Surface the backend's error message when it returns JSON.
    let detail = response.statusText;
    try {
      const data = await response.clone().json();
      detail = data?.error || data?.message || detail;
    } catch {
      /* response was not JSON */
    }
    throw new Error(`PDF download failed (${response.status}): ${detail}`);
  }

  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error("PDF download failed: empty response");
  }
  return blob;
};

const kbIssueApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getKbIssues: builder.query<any, Record<string, any> | void>({
      query: (params) => {
        const queryString = params
          ? new URLSearchParams(flattenQueryParams(params)).toString()
          : "";
        return `/gw/kb-issues${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: [kbIssueTag],
    }),
    getKbIssueById: builder.query<any, number>({
      query: (id) => `/gw/kb-issues/${id}`,
      providesTags: (result, error, id) => [{ type: kbIssueTag, id }],
    }),
    createKbIssue: builder.mutation<KbIssue, Partial<KbIssue>>({
      query: (data) => ({
        url: `/gw/kb-issues`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [kbIssueTag],
    }),
    updateKbIssue: builder.mutation<KbIssue, { id: number } & Partial<KbIssue>>({
      query: ({ id, ...data }) => ({
        url: `/gw/kb-issues/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        kbIssueTag,
        { type: kbIssueTag, id },
      ],
    }),
    deleteKbIssue: builder.mutation<void, number>({
      query: (id) => ({
        url: `/gw/kb-issues/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [kbIssueTag],
    }),
    linkSolutionToIssue: builder.mutation<
      void,
      { issueId: number; kb_solution_id: number; language: "en" | "tr" | "english" | "turkish" }
    >({
      query: ({ issueId, kb_solution_id, language }) => ({
        url: `/gw/kb-issues/${issueId}/solutions`,
        method: "POST",
        body: { kb_solution_id, language },
      }),
      invalidatesTags: (result, error, { issueId }) => [
        kbIssueTag,
        { type: kbIssueTag, id: issueId },
      ],
    }),
    unlinkSolutionFromIssue: builder.mutation<
      void,
      { issueId: number; solutionId: number; language: "en" | "tr" | "english" | "turkish" }
    >({
      query: ({ issueId, solutionId, language }) => ({
        url: `/gw/kb-issues/${issueId}/solutions/${solutionId}?language=${encodeURIComponent(language)}`,
        method: "DELETE",
        body: { language },
      }),
      invalidatesTags: (result, error, { issueId }) => [
        kbIssueTag,
        { type: kbIssueTag, id: issueId },
      ],
    }),
    bulkApprovePreCheck: builder.mutation<BulkApprovePreCheckResult, { issue_ids: number[] }>({
      query: (data) => ({
        url: `/gw/kb-issues/bulk-approve/pre-check`,
        method: "POST",
        body: data,
      }),
    }),
    bulkApprove: builder.mutation<
      BulkApproveResult,
      {
        issue_ids: number[];
        approve_solutions: boolean;
        language: "en" | "tr" | "english" | "turkish";
      }
    >({
      query: (data) => ({
        url: `/gw/kb-issues/bulk-approve`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [kbIssueTag, "KbSolution"],
    }),
    reVectorize: builder.mutation<
      { message: string },
      { issue_ids: number[]; language: "en" | "tr" | "english" | "turkish" }
    >({
      query: (data) => ({
        url: `/gw/kb-issues/re-vectorize`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [kbIssueTag, "KbSolution"],
    }),
  }),
});

interface PreCheckItem {
  id: number;
  text: string;
}

export interface BulkApprovePreCheckResult {
  issuesWithoutSolutions: PreCheckItem[];
  issuesWithUnapprovedSolutions: PreCheckItem[];
  unapprovedSolutions: PreCheckItem[];
  approvableIssues: PreCheckItem[];
  totalIssues: number;
}

export interface BulkApproveResult {
  approvedIssueCount: number;
  approvedSolutionCount: number;
}

export const {
  useGetKbIssuesQuery,
  useGetKbIssueByIdQuery,
  useCreateKbIssueMutation,
  useUpdateKbIssueMutation,
  useDeleteKbIssueMutation,
  useLinkSolutionToIssueMutation,
  useUnlinkSolutionFromIssueMutation,
  useBulkApprovePreCheckMutation,
  useBulkApproveMutation,
  useReVectorizeMutation,
} = kbIssueApi;
